import { useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';

import { BusinessOutlined, PersonOutline } from '@mui/icons-material';
import { Avatar, Box, Popover, SxProps, Theme, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { currentUserApi } from '../../axios';
import { useFeatureFlags, useLanguage, usePermissionFlags } from '../../hooks';
import { LogOutOutline } from '../../icons';
import { useUserDataStore } from '../../newStore/userDataStore';
import { paths, redirectTo } from '../../utils/navigation';
import { isSupportOrAdmin } from '../../utils/utils';
import { MfaModal } from '../MfaModal/MfaModal';
import { LangSelector } from '../uiComponents/CommonComponents/LangSelector';
import { UserActionsOverview } from './UserActionsOverview';

const customStyles: Record<string, SxProps<Theme>> = {
  avatar: {
    width: '40px',
    height: '40px',
    backgroundColor: (theme) => theme.palette.primary.main,
    color: '#FFFFFF',
    fontSize: '18px',
    fontWeight: 600,
  },
  popover: {
    marginTop: '8px',
    '& .MuiPopover-paper': {
      borderRadius: '12px',
    },
  },
  popoverContent: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  popoverItem: {
    fontSize: '14px',
    fontWeight: '400',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      color: (theme) => theme.palette.primary.main,
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  iconSize: {
    fontSize: '16px',
  },
};

export function UserNav() {
  const lang = useLanguage();
  const currentUser = useUserDataStore((state) => state.currentUser);
  const [searchParams] = useSearchParams();
  const isFromMobileApp = searchParams.get('redirect_to_frontend') === 'true';
  const pdfMode = searchParams.get('pdf_mode') ? true : false;

  const { logoutUser } = useUserDataStore((state) => state.actions);
  const { enqueueSnackbar } = useSnackbar();
  const { checkFlag } = useFeatureFlags();
  const { checkPermission } = usePermissionFlags();

  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openMfaModal, setOpenMfaModal] = useState(false);
  const [lastMfaStatus, setLastMfaStatus] = useState<boolean | null>(null);

  // Check MFA status and auto-open modal if needed
  useEffect(() => {
    const checkMfaStatus = async () => {
      if (
        !currentUser?.id ||
        currentUser?.auth_type !== 'cognito' ||
        !checkFlag('MFACognito') ||
        pdfMode
      ) {
        return;
      }

      const justLoggedIn = sessionStorage.getItem(`justLoggedIn-${currentUser?.id}`);
      if (justLoggedIn === 'true') {
        return;
      }

      try {
        const data = await currentUserApi.getMfaSettingsStatus();

        if (data.settings === 'mandatory' || data.settings === 'optional') {
          const mfaStatus = await currentUserApi.getMfaStatus();

          // Reset dismissal state if MFA status changed
          if (lastMfaStatus !== null && lastMfaStatus !== mfaStatus.mfa_enabled) {
            sessionStorage.setItem(`justLoggedIn-${currentUser?.id}`, 'false');
          }
          setLastMfaStatus(mfaStatus.mfa_enabled);

          if (!mfaStatus.mfa_enabled) {
            sessionStorage.setItem(`justLoggedIn-${currentUser?.id}`, 'true');
            setOpenMfaModal(true);
          }
        }
      } catch (error) {
        console.error('Error checking MFA status:', error);
      }
    };

    checkMfaStatus();
  }, []);

  if (!currentUser?.id && !currentUser?.is_support) return <></>;

  const isFreeUser = !currentUser?.is_paying;
  const isAdmin = currentUser?.is_admin;
  const hasFullAccess = isSupportOrAdmin(currentUser);

  const userHasNewBilling = (checkFlag('ActionsOverview') && !isFreeUser) || isFreeUser;
  const userHasPermissionsForActionsOverview = hasFullAccess || isAdmin;
  const actionsOverviewEnabled = userHasNewBilling && userHasPermissionsForActionsOverview;

  const handleDeleteJustLoggedIn = () => {
    if (currentUser?.id && currentUser?.auth_type === 'cognito') {
      try {
        sessionStorage.removeItem(`justLoggedIn-${currentUser.id}`);
      } catch (error) {
        console.error('Failed to remove justLoggedIn item:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      setUserMenuAnchorEl(null);
      handleDeleteJustLoggedIn();
      await currentUserApi.logout();
      logoutUser();
      redirectTo('', true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(lang.login.logoutError, { variant: 'error' });
    }
  };

  return (
    <>
      <Avatar
        sx={customStyles.avatar}
        onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
      >
        {`${currentUser?.username?.charAt(0)}${currentUser?.last_name?.charAt(0)}`}
      </Avatar>
      <Popover
        open={!!userMenuAnchorEl}
        onClose={() => setUserMenuAnchorEl(null)}
        anchorEl={userMenuAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={customStyles.popover}
      >
        <Box sx={customStyles.popoverContent}>
          {actionsOverviewEnabled && <UserActionsOverview />}
          <LangSelector />
          {(checkPermission('admin_account_config') || hasFullAccess) && (
            <Typography
              component={NavLink}
              to={paths.account.index}
              sx={customStyles.popoverItem}
              onClick={() => setUserMenuAnchorEl(null)}
            >
              <BusinessOutlined
                fontSize='inherit'
                color='inherit'
                sx={customStyles.iconSize}
              />
              {lang.mainTopBar.companySettings}
            </Typography>
          )}
          <Typography
            component={NavLink}
            to={`${paths.users.index}/${currentUser?.id}`}
            sx={customStyles.popoverItem}
            onClick={() => setUserMenuAnchorEl(null)}
          >
            <PersonOutline
              fontSize='inherit'
              color='inherit'
              sx={customStyles.iconSize}
            />
            {lang.mainTopBar.myAccount}
          </Typography>
          <Typography
            sx={customStyles.popoverItem}
            onClick={handleLogout}
          >
            <LogOutOutline
              fontSize='inherit'
              color='inherit'
              sx={customStyles.iconSize}
            />
            {lang.mainTopBar.logout}
          </Typography>
        </Box>
      </Popover>
      <MfaModal
        open={openMfaModal}
        showActivateInSuccess={!isFromMobileApp}
        onClose={() => setOpenMfaModal(false)}
        isMobile={isFromMobileApp}
      />
    </>
  );
}
